/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
// eslint-disable-next-line no-restricted-imports
import isUndefined from 'lodash.isundefined';
import Router, {NextRouter} from 'next/router';
import qs from 'qs';
import {getModifiedPageTitlesForMobile} from '@ion/applications/src/routes/RouteUtils';
import {localPersistence} from '@ion/core/src/capabilities/storage';
import {translate} from '@ion/core/src/locale/locale';
import {info} from '@ion/core/src/logger/logger';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {ApplicationKeys} from '@/types/application';
import {
  ADMIN,
  ADMIN_ACCOUNTS,
  ADMIN_AEMP_CONFIG,
  ADMIN_APPLICATIONS,
  ADMIN_ASSETS,
  ADMIN_DEVICE_CONFIGS,
  ADMIN_GROUPS,
  ADMIN_PEG_BEHAVIORS,
  ADMIN_ROLES,
  ADMIN_USERS,
  ALERTS_NOTIFICATIONS,
  ALERTS_SETUP,
  DASHBOARD_PAGE,
  DEVICES,
  MANUFACTURED_DEVICES,
  MANUFACTURING,
  MANUFACTURING_APPLICATION_IDS,
  MANUFACTURING_INVENTORY,
  MANUFACTURING_PART_NUMBERS,
  MANUFACTURING_SHIPMENTS,
  OEM_VALIDATION_RULES,
  PROVISIONING,
  PROVISIONING_DEVICE_FILES,
  PROVISIONING_DOWNLOAD_JOBS,
  PROVISIONING_JOBS_SEARCH_ENGINE,
  REPORTS_AVAILABLE_REPORTS,
  REPORTS_SCHEDULED_REPORTS,
  SENSOR_INSTALLER,
  SENSORS,
  SYSTEM_CONFIG_ALERT_TYPES,
  SYSTEM_CONFIG_DEVICE_TYPES,
  TOOLBOX,
  TOOLBOX_KEYFILE_BUILDER,
  TOOLBOX_MODBUS,
  VEHICLE,
  VEHICLE_MANAGER,
  VEHICLE_PROFILE
} from '../security/Features';

// defines all predefined route definitions
export enum Route {
  ADMIN = 'admin',
  ADMIN_APPLICATIONS = 'admin/applications',
  ADMIN_ASSETS = 'admin/assets',
  ADMIN_ACCOUNTS = 'admin/accounts',
  ADMIN_AEMP_CONFIG = 'admin/aempconfig',
  ADMIN_DEVICE_CONFIGS = 'admin/deviceconfigs',
  ADMIN_GROUPS = 'admin/groups',
  ADMIN_NOTIFICATIONS = 'admin/notifications',
  ADMIN_PEG_BEHAVIORS = 'admin/pegbehaviors',
  ADMIN_PEG_LABELS = 'admin/peglabels',
  ADMIN_USERS = 'admin/users',
  ADMIN_ROLES = 'admin/roles',
  DASHBOARD = 'dashboard',
  DEVICES = 'devices',
  LOGIN = 'login',
  PROVISIONING = 'provisioning',
  NOTIFICATIONS = 'notifications',
  PROVISIONING_DEVICE_FILES = 'provisioning/devicefiles',
  PROVISIONING_DOWNLOAD_JOBS = 'provisioning/downloadjobs',
  PROVISIONING_JOBS_SEARCH_ENGINE = 'provisioning/jobsearchengine',
  REPORTS = 'reports',
  ANALYTICS = 'analytics',
  ANALYTICS_DASHBOARDS = 'reports/analytics/dashboards',
  ANALYTICS_SCHEDULER = 'reports/analytics/scheduler',
  SYSTEM_CONFIG = 'systemconfig',
  SYSTEM_CONFIG_ALERT_TYPES = 'systemconfig/notificationtype',
  SYSTEM_CONFIG_DEVICE_TYPES = 'systemconfig/devicetypes',
  TOOLBOX = 'toolbox',
  TOOLBOX_KEYFILE_BUILDER = 'toolbox/keyfileBuilder',
  TOOLBOX_MODBUS_EDITOR = 'toolbox/modbusEditor',
  TOOLBOX_MODBUS = 'toolbox/modbus',
  MANUFACTURING = 'manufacturing',
  MANUFACTURED_DEVICES = 'manufacturing/devices',
  MANUFACTURING_INVENTORY = 'manufacturing/provisionedDevices',
  MANUFACTURING_APPLICATION_IDS = 'manufacturing/applicationIds',
  MANUFACTURING_PART_NUMBERS = 'manufacturing/partNumbers',
  MANUFACTURING_SHIPMENTS = 'manufacturing/shipments',
  VEHICLE = 'vehicle',
  VEHICLE_MANAGER = 'vehicle/manager',
  VEHICLE_PROFILE = 'vehicle/profile',
  OEM_VALIDATION_RULES = 'vehicle/validation',
  SENSORS = 'sensors',
  SENSOR_INSTALLER = 'sensor/installer'
};

/**
 * Translations from Routes to Translation Keys
 */
export const RouteToTranslationKey = {
  [Route.ADMIN]                           : 'admin',
  [Route.ADMIN_APPLICATIONS]              : 'adminApplications',
  [Route.ADMIN_ASSETS]                    : 'adminAssets',
  [Route.ADMIN_ACCOUNTS]                  : 'adminAccounts',
  [Route.ADMIN_AEMP_CONFIG]               : 'adminAempconfig',
  [Route.ADMIN_DEVICE_CONFIGS]            : 'adminDeviceConfigs',
  [Route.ADMIN_GROUPS]                    : 'adminGroups',
  [Route.ADMIN_NOTIFICATIONS]             : 'adminNotifications',
  [Route.ADMIN_PEG_BEHAVIORS]             : 'adminPegBehaviors',
  [Route.ADMIN_PEG_LABELS]                : 'adminPegLabels',
  [Route.ADMIN_USERS]                     : 'adminUsers',
  [Route.DASHBOARD]                       : 'dashboard',
  [Route.DEVICES]                         : 'devices',
  [Route.LOGIN]                           : 'login',
  [Route.ADMIN_ROLES]                     : 'adminRoles',
  [Route.PROVISIONING]                    : 'provisioning',
  [Route.PROVISIONING_DEVICE_FILES]       : 'provisioningDevicefiles',
  [Route.PROVISIONING_DOWNLOAD_JOBS]      : 'provisioningDownloadjobs',
  [Route.PROVISIONING_JOBS_SEARCH_ENGINE] : 'provisioningJobSearchEngine',
  [Route.NOTIFICATIONS]                   : 'notifications',
  [Route.SYSTEM_CONFIG]                   : 'systemConfig',
  [Route.SYSTEM_CONFIG_ALERT_TYPES]       : 'systemconfigNotificationType',
  [Route.SYSTEM_CONFIG_DEVICE_TYPES]      : 'systemconfigDevicetypes',
  [Route.REPORTS]                         : 'reports',
  [Route.TOOLBOX]                         : 'toolbox',
  [Route.TOOLBOX_KEYFILE_BUILDER]         : 'toolboxKeyfileBuilder',
  [Route.TOOLBOX_MODBUS]                  : 'toolboxModBus',
  [Route.TOOLBOX_MODBUS_EDITOR]           : 'toolboxModbusEditor',
  [Route.MANUFACTURING]                   : 'manufacturing',
  [Route.MANUFACTURED_DEVICES]            : 'manufacturedDevices',
  [Route.MANUFACTURING_INVENTORY]         : 'manufacturingInventory',
  [Route.MANUFACTURING_APPLICATION_IDS]   : 'manufacturingApplicationIds',
  [Route.MANUFACTURING_PART_NUMBERS]      : 'manufacturingPartNumbers',
  [Route.ANALYTICS_DASHBOARDS]            : 'analyticsDashboards',
  [Route.ANALYTICS_SCHEDULER]             : 'analyticsReportManager',
  [Route.MANUFACTURING_SHIPMENTS]         : 'manufacturingShipments',
  [Route.VEHICLE]                         : 'vehicle',
  [Route.VEHICLE_MANAGER]                 : 'vehicleManager',
  [Route.VEHICLE_PROFILE]                 : 'vehicleProfile',
  [Route.OEM_VALIDATION_RULES]            : 'validation',
  [Route.SENSORS]                         : 'sensors',
  [Route.SENSOR_INSTALLER]                : 'sensorInstaller'
};

const FeaturesMap = {
  [DASHBOARD_PAGE]                  : Route.DASHBOARD,
  [DEVICES]                         : Route.DEVICES,
  [PROVISIONING]                    : Route.PROVISIONING,
  [PROVISIONING_DEVICE_FILES]       : Route.PROVISIONING_DEVICE_FILES,
  [PROVISIONING_DOWNLOAD_JOBS]      : Route.PROVISIONING_DOWNLOAD_JOBS,
  [PROVISIONING_JOBS_SEARCH_ENGINE] : Route.PROVISIONING_JOBS_SEARCH_ENGINE,
  [ALERTS_NOTIFICATIONS]            : Route.NOTIFICATIONS,
  [ADMIN]                           : Route.ADMIN,
  [ADMIN_APPLICATIONS]              : Route.ADMIN_APPLICATIONS,
  [ADMIN_ASSETS]                    : Route.ADMIN_ASSETS,
  [ADMIN_ACCOUNTS]                  : Route.ADMIN_ACCOUNTS,
  [ADMIN_AEMP_CONFIG]               : Route.ADMIN_AEMP_CONFIG,
  [ADMIN_USERS]                     : Route.ADMIN_USERS,
  [ADMIN_GROUPS]                    : Route.ADMIN_GROUPS,
  [ADMIN_ROLES]                     : Route.ADMIN_ROLES,
  [ADMIN_PEG_BEHAVIORS]             : Route.ADMIN_PEG_BEHAVIORS,
  [ADMIN_DEVICE_CONFIGS]            : Route.ADMIN_DEVICE_CONFIGS,
  [ALERTS_SETUP]                    : Route.ADMIN_NOTIFICATIONS,
  [SYSTEM_CONFIG_ALERT_TYPES]       : Route.SYSTEM_CONFIG_ALERT_TYPES,
  [SYSTEM_CONFIG_DEVICE_TYPES]      : Route.SYSTEM_CONFIG_DEVICE_TYPES,
  [REPORTS_AVAILABLE_REPORTS]       : Route.ANALYTICS_DASHBOARDS,
  [REPORTS_SCHEDULED_REPORTS]       : Route.ANALYTICS_SCHEDULER,
  [TOOLBOX]                         : Route.TOOLBOX,
  [TOOLBOX_KEYFILE_BUILDER]         : Route.TOOLBOX_KEYFILE_BUILDER,
  [TOOLBOX_MODBUS]                  : Route.TOOLBOX_MODBUS,
  [MANUFACTURING]                   : Route.MANUFACTURING,
  [MANUFACTURED_DEVICES]            : Route.MANUFACTURED_DEVICES,
  [MANUFACTURING_INVENTORY]         : Route.MANUFACTURING_INVENTORY,
  [MANUFACTURING_APPLICATION_IDS]   : Route.MANUFACTURING_APPLICATION_IDS,
  [MANUFACTURING_PART_NUMBERS]      : Route.MANUFACTURING_PART_NUMBERS,
  [MANUFACTURING_SHIPMENTS]         : Route.MANUFACTURING_SHIPMENTS,
  [VEHICLE]                         : Route.VEHICLE,
  [VEHICLE_MANAGER]                 : Route.VEHICLE_MANAGER,
  [VEHICLE_PROFILE]                 : Route.VEHICLE_PROFILE,
  [OEM_VALIDATION_RULES]            : Route.OEM_VALIDATION_RULES,
  [SENSORS]                         : Route.SENSORS,
  [SENSOR_INSTALLER]                : Route.SENSOR_INSTALLER
};

/**
 * Provides the initial point of entry for configuring CTC Admin Route management.
 *
 * Invoked when the root application component (_app.js) is first mounted, after which
 * all query changes are referenced for subsequent inclusion within each route when the
 * current page changes.
 */
export const initializeRouter = () => {
  info('Initializing Application Router ...');
  setQuery();
  Router.events.on('routeChangeStart', function() {
    setQuery();
  });
};

/**
 * Provides the single point of entry for changing a page within CTC Admin. This function
 * must be used explicitly and Next Router may not be used directly.
 *
 * The changePage function allows for ensuring all pages meet the requirements of CTC Admin,
 * including, but not limited to, verifying Route definitions, inclusion of existing query
 * parameters, etc..
 *
 * ```typescript
 *
 *   import changePage {Route} from '@/routes/RouteDefinitions';
 *
 *   // navigate to the admin assets page, including existing query parameters by
 *   // default ...
 *   changePage(Route.ADMIN_ASSETS);
 *
 *   // navigate to a specific reports page (e.g. /reports/123), including existing
 *   // query parameters, by default ...
 *   changePage(Route.REPORTS, `/${data.id}`);
 *
 *   // navigate to the admin assets page, excluding existing query parameters ...
 *   changePage(Route.ADMIN_ASSETS, null, false);
 *
 *   // navigate to a specific reports page (e.g. /reports/123), excluding existing
 *   // query parameters ....
 *   changePage(Route.REPORTS, `/${data.id}`, false);
 *
 * ```
 *
 * @param {Route} route The Route to which the application is to navigate.
 * @param {string} path An optional path to include within the route.
 * @param {boolean} includeParams When true (default), the previous pages query parameters
 */
export const changePage = (route: Route, path = '', includeParams = true): void => {
  let url: string = getRoute(route, path);
  if (!isObjectEmpty(_query)) {
    if (!includeParams) {
      const {env, features} = _query;
      if (env || features) {
        url = `${url}?${qs.stringify({env, features})}`;
      }
    } else {
      url = `${url}?${qs.stringify(_query)}`;
    }
  }
  Router.push(url);
};

/**
 * The getRoute function provides the single point of access which should be used
 * for retrieving a given route. The helper ensures that the route being referenced
 * has the correct base path prefix and maps to an existing, predefined route.
 *
 * @param {Route} route The Route to which the application is to navigate.
 * @param {string} path An optional path to include within the route.
 */
export const getRoute = (route: Route, path?: string): string => {
  return `/${route?.replace?.(/^\/+/, '')}${path || ''}`.split('?')?.[0];
};

/**
 * Provides a utility which returns the currently persisted application route
 * from local storage.
 *
 * @returns the currently persisted application `Route`.
 */
export const getPersistedRoute = (): Route => {
  return localPersistence(ApplicationKeys.CTCAdminPage, 'route') as Route;
};

/**
 * Provides a utility which sets the currently persisted application route to
 * local storage.
 *
 * @param the route which is to be persisted to local storage.
 */
export const setPersistedRoute = (page: string): void => {
  localPersistence(ApplicationKeys.CTCAdminPage, 'route', page?.split('?')?.[0]);
};

/**
 * @private
 */
let _query;

/**
 * Provides a reference to the current query parameters passed to the current page,
 * or forwarded to the current page from subsequent pages.
 */
export const getQuery = () => {
  return _query;
};

/**
 * Allows for setting the query parameters to be passed to each CTC Admin page when
 * the page is loaded. This function should generally not be used directly as it is
 * primarily used internally for updating the query parameters passed to CTC Admin
 * pages.
 */
export const setQuery = (querystring?: string): void => {
  _query = qs.parse(querystring || location.search, {ignoreQueryPrefix: true});
};

/**
 * Provides a utility for determining if the application is being run in debug mode
 * via the `?debug? Querystring parameter.
 *
 * This function is primarily intended for determining if the application is to be
 * run in debug mode prior to initializing the router in order to enable / disable
 * logging in higher environments.
 */
export const isDebug = (): boolean => {
  const query = getQuery() || qs.parse(location.search, {ignoreQueryPrefix: true});
  return !isUndefined(query?.debug);
};

/**
 * Provides a utility which determines if the provided route is the current route.
 */
export const isRoute = (route: Route): boolean => {
  return window.location.pathname.indexOf(route) > -1;
};

/**
 * Provides a utility which determines if the given `Route` or path is valid.
 *
 * @param   route Either a pre-defined `Route` or URI path.
 * @returns true if the provides route or path is valid, otherwise, false.
 */
export const isValidPath = (route: Partial<NextRouter>): boolean => {
  return !!(route?.pathname && route?.pathname !== '/');
};

/**
 * The getRouteDefinition function provides a utility which returns the Route for
 * the given path
 *
 * ```typescript
 *    import {getRouteDefinition} from '@/routes/RouteDefinitions';
 *
 *    getRouteDefinition('/tracking'); // Route.TRACKING
 *    getRouteDefinition('/admin/assets'); // Route.ADMIN_ASSETS
 *
 * ```
 */
export const getRouteDefinition = (path: string): Route => {
  const route = path?.replace(/^\/|\/$/g, '').split('?')?.[0];
  const name  = Object.keys(Route).find((name: string) => {
    return Route[name] === route;
  });
  return Route[name];
};

/**
 * The getRouteByFeature function provides a utility which returns the iOn Route for
 * the given iOn Feature.
 *
 * ```typescript
 *    import {getRouteDefinition} from '@/routes/RouteDefinitions';
 *
 *    getRouteByFeature(TRACKING); // Route.TRACKING
 *    getRouteByFeature(ADMIN_ASSETS); // Route.ADMIN_ASSETS
 *
 * ```
 */
export const getRouteByFeature = (id: string, fallback = true): Route => {
  let route = FeaturesMap[id];
  if (!route && fallback) {
    route = Route.DASHBOARD;
  }
  return route;
};

/**
 * Utility that returns the translated route Label for the given Route
 *
 * ```typescript
 *    import {getRouteLabel} from '@/routes/RouteDefinitions';
 *
 *    getRouteDefinition(Route.ADMIN);             // Admin
 *    getRouteDefinition(Route.ADMIN_AEMP_CONFIG); // AEMP Config
 *    getRouteDefinition(Route.ADMIN_DEVICES);     // Devices
 *    getRouteDefinition(Route.DASHBOARD);         // Dashboard
 *    getRouteDefinition(Route.SYSTEM_CONFIG);     // System Config
 *
 * ```
 */
export const getRouteLabel = (route: Route): string => {
  return translate(`nav:side.${RouteToTranslationKey[route]}`);
};

/**
 * The getPageTitle function provides a simple utility which returns the page title
 * for the given route.
 *
 * ```typescript
 *   import {getPageTitle} from '@/routes/RouteDefinitions';
 *
 *   getPageTitle(Route.ADMIN_ASSETS); // Assets
 *   getPageTitle(Route.TRACKING); // Tracking
 *
 * ```
 */
export const getPageTitle = (route: Route | string) => {
  let [, prefix, postfix] = route.substr(0, route.length).split('/');
  // if postfix contains a string integer just display the prefix
  if (parseInt(postfix)) {
    return translate(`nav:side.${prefix}`);
  }
  if (postfix) {
    postfix                 = `${postfix.charAt(0).toUpperCase()}${postfix.slice(1)}`;
    const modifiedPageTitle = getModifiedPageTitlesForMobile(`${prefix}${postfix}`);
    return translate(`nav:side.${modifiedPageTitle}`);
  }
  return translate(`nav:side.${prefix}`);
};

export default changePage;
